/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'
import {useNavigate} from 'react-router-dom'
import Searchbar from 'app/components/Searchbar'
import {CloudListToolbar} from './components/header/CloudListToolbar'
import {useAuth} from 'app/modules/auth'
import cloudService from './services/cloud.services'
import EcosystemIcon from 'app/components/EcosystemIcon'
import EmptyState from 'app/components/EmptyState'
import TimeAgo from 'app/components/TimeAgo'
import ConfirmationModal from 'app/components/ConfirmationModal'
import EditConfigurationModal from './components/modal/EditConfigurationModal'
import { showErrorToast, showSuccessToast, showLoadingToast, updateLoadingToast, dismissLoadingToast } from 'app/components/ShowToast'

type Props = {
  className: string
}

interface CloudData {
  id: string
  type: string
  purpose: string
  name: string
  account_id: string
  severity: string
  open_issues: string
  ignored: string
  last_scanned: any
  created_at: any
}

const Cloud: React.FC<Props> = ({className}) => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [clouds, setClouds] = useState<CloudData[]>([])
  const [filteredClouds, setFilteredClouds] = useState<CloudData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [cloudToDelete, setCloudToDelete] = useState<CloudData | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [cloudToEdit, setCloudToEdit] = useState<CloudData | null>(null)

  useEffect(() => {
    if (!currentUser?.tenantId) {
      setIsLoading(false)
      return
    }

    const unsubscribe = cloudService.getClouds(currentUser.tenantId, (cloudsData) => {
      setClouds(cloudsData)
      setFilteredClouds(cloudsData)
      setIsLoading(false)
    })

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [currentUser?.tenantId])

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredClouds(clouds)
    } else {
      const filtered = clouds.filter(
        (cloud) =>
          cloud.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cloud.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cloud.purpose.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cloud.account_id.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredClouds(filtered)
    }
  }, [searchTerm, clouds])

  const toggleDropdown = (cloudId: string) => {
    setOpenDropdownId(openDropdownId === cloudId ? null : cloudId)
  }

  const handleRowClick = (cloudId: string) => {
    setOpenDropdownId(null)
    navigate(`/vulnerabilities/clouds/${cloudId}`)
  }

  const handleDeleteClick = (cloud: CloudData, e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenDropdownId(null)
    setCloudToDelete(cloud)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!cloudToDelete || !currentUser?.tenantId) return

    setIsDeleting(true)
    try {
      await cloudService.deleteCloud(currentUser.tenantId, cloudToDelete.id)
      setClouds(prevClouds => prevClouds.filter(cloud => cloud.id !== cloudToDelete.id))
      setFilteredClouds(prevFiltered => prevFiltered.filter(cloud => cloud.id !== cloudToDelete.id))
      setIsDeleteModalOpen(false)
      setCloudToDelete(null)
      showSuccessToast({message: `Cloud connection "${cloudToDelete.name}" has been deleted.`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the cloud connection.'})
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
    setCloudToDelete(null)
  }

  const handleEditClick = (cloud: CloudData, e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenDropdownId(null)
    setCloudToEdit(cloud)
    setIsEditModalOpen(true)
  }

  const handleEditClose = () => {
    setIsEditModalOpen(false)
    setCloudToEdit(null)
  }

  const handleCloudUpdate = (updatedCloud: CloudData) => {
    setClouds(prevClouds => 
      prevClouds.map(cloud => 
        cloud.id === updatedCloud.id ? updatedCloud : cloud
      )
    )
    setFilteredClouds(prevFiltered => 
      prevFiltered.map(cloud => 
        cloud.id === updatedCloud.id ? updatedCloud : cloud
      )
    )
  }

  const handleStartScan = async (cloud: CloudData, e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenDropdownId(null)
    
    if (!currentUser?.tenantId) {
      showErrorToast({message: 'No tenant ID found'})
      return
    }
  
    const now = new Date()
    const lastScannedDate = cloud.last_scanned?.toDate ? cloud.last_scanned.toDate() : new Date(cloud.last_scanned)
    const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000)
  
    if (lastScannedDate && lastScannedDate > twelveHoursAgo) {
      const timeDiffMs = now.getTime() - lastScannedDate.getTime()
      const timeDiffHours = timeDiffMs / (1000 * 60 * 60)
      showErrorToast({
        message: `Scan for ${cloud.name} was run recently. Please wait ${Math.ceil(12 - timeDiffHours)} hours before scanning again.`
      })
      return
    }
  
    let toastId
    try {
      toastId = showLoadingToast({ message: `Starting scan for ${cloud.name}...` })
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      updateLoadingToast(toastId, {
        message: 'Running Cloud scan...',
        type: 'info',
        isLoading: true,
      })
      
      const scanResults = await cloudService.runCloudSploitScanFromCloudId(currentUser.tenantId, cloud.id)
      
      if (scanResults && scanResults.success) {
        updateLoadingToast(toastId, {
          message: 'Scan completed, updating results...',
          type: 'info',
          isLoading: true,
        })
  
        const updatedCloud = await cloudService.getCloudById(currentUser.tenantId, cloud.id)
        if (updatedCloud) {
          handleCloudUpdate(updatedCloud as CloudData)
        }
        
        dismissLoadingToast(toastId)
        showSuccessToast({message: `Scan completed successfully for ${cloud.name}`})
      } else {
        dismissLoadingToast(toastId)
        showErrorToast({message: `Scan failed for ${cloud.name}. Please check your configuration.`})
      }
    } catch (error) {
      dismissLoadingToast(toastId)
      showErrorToast({message: `An error occurred while scanning ${cloud.name}`})
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'text-danger fw-bold'
      case 'high':
        return 'text-warning fw-bold'
      case 'medium':
        return 'text-info fw-bold'
      case 'low':
        return 'text-success fw-bold'
      default:
        return 'text-muted'
    }
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '50vh'}}>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Vulnerabilities</title>
      </Helmet>
      <div className='card-header border-0 pt-6 h-100'>
        <div className='card-title'>
          <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        <div className='card-toolbar'>
          <CloudListToolbar />
        </div>
      </div>
      <div className='card-body py-3 '>
        {filteredClouds.length === 0 ? (
          <EmptyState
            title={searchTerm ? 'No clouds found' : 'No clouds available'}
            description={
              searchTerm
                ? 'No clouds found matching your search criteria.'
                : 'You haven\'t connected any cloud providers yet. Click the "Connect Cloud" button to get started.'
            }
            isLoading={false}
          />
        ) : (
          <div className='table-responsive' style={{height: '55vh', overflowY: 'auto'}}>
            <table className='table align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold fs-7 border-bottom border-dotted' style={{color: '#5A5A5A'}}>
                  <th className='ps-4 min-w-100px rounded-start'>TYPE</th>
                  <th className='min-w-125px'>PURPOSE</th>
                  <th className='min-w-125px'>NAME</th>
                  <th className='min-w-150px'>ACCOUNT ID</th>
                  <th className='min-w-100px'>SEVERITY</th>
                  <th className='min-w-125px'># OPEN ISSUES</th>
                  <th className='min-w-125px'>LAST SCAN</th>
                  <th className='min-w-50px text-end rounded-end'></th>
                </tr>
              </thead>

              <tbody>
                {filteredClouds.map((cloud) => (
                  <tr
                    key={cloud.id}
                    className='border-bottom border-dotted'
                    onClick={() => handleRowClick(cloud.id)}
                    style={{cursor: 'pointer'}}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <td>
                      <div className='d-flex align-items-center mx-5'>
                        <EcosystemIcon ecosystem={cloud.type} className='me-2' size={24} />
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>{cloud.purpose || 'N/A'}</div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>{cloud.name || 'N/A'}</div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>{cloud.account_id || 'N/A'}</div>
                    </td>
                    <td>
                      <div
                        className={`d-flex align-items-center ${getSeverityColor(cloud.severity)}`}
                      >
                        {cloud.severity || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>{cloud.open_issues || '0'}</div>
                    </td>
                    <td>
                      <TimeAgo timestamp={cloud.last_scanned} />
                    </td>
                    <td>
                      <div className='dropdown mx-2'>
                        <button
                          className='btn btn-secondary border-0 p-0 m-2'
                          type='button'
                          id={`dropdownMenu-${cloud.id}`}
                          data-bs-toggle='dropdown'
                          aria-haspopup='true'
                          aria-expanded={openDropdownId === cloud.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDropdown(cloud.id)
                          }}
                        >
                          <img
                            src='/media/buttons/Options.svg'
                            alt='Options'
                          />
                        </button>
                        <ul
                          className={`dropdown-menu ${openDropdownId === cloud.id ? 'show' : ''}`}
                          aria-labelledby={`dropdownMenu-${cloud.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <li>
                            <button
                              className='dropdown-item'
                              type='button'
                              onClick={(e) => handleStartScan(cloud, e)}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-shield-search me-2'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M12 21a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3c.539 1.832 .627 3.747 .283 5.588' />
                                <path d='M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
                                <path d='M20.2 20.2l1.8 1.8' />
                              </svg>
                              Start scan
                            </button>
                          </li>
                          <li>
                            <button
                              className='dropdown-item'
                              type='button'
                              onClick={(e) => handleEditClick(cloud, e)}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-edit me-2'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                                <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                                <path d='M16 5l3 3' />
                              </svg>
                              Edit configuration
                            </button>
                          </li>
                          <li>
                            <button
                              className='dropdown-item text-danger'
                              type='button'
                              onClick={(e) => handleDeleteClick(cloud, e)}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-trash me-2'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M4 7l16 0' />
                                <path d='M10 11l0 6' />
                                <path d='M14 11l0 6' />
                                <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                              </svg>
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={handleDeleteCancel}
        confirmBtnClickHandler={handleDeleteConfirm}
        toDeleteItemTitle={cloudToDelete?.name}
        isLoading={isDeleting}
        title="Delete Cloud Connection"
        message={`Are you sure you want to delete "${cloudToDelete?.name}"? This will permanently remove the cloud connection and all associated data.`}
        confirmButtonText="Delete"
        type="delete"
      />

      <EditConfigurationModal
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
        cloud={cloudToEdit}
        onUpdate={handleCloudUpdate}
        onDelete={(cloud) => {
          setCloudToDelete(cloud)
          setIsDeleteModalOpen(true)
        }}
      />
    </>
  )
}

export {Cloud}
